import React, { useEffect, useMemo, useState } from "react";
import CreatePostBox from "./CreatePost";
import { useAppContext } from "../../Context/AppContext";
import PostCard from "./PostCard";
import { Image } from "react-bootstrap";
import { useFindUpcomingEvents } from "../../api/queries/useFindUpcomingEvents";
import { Event, Post } from "../../types/Types";
import {
  isEventDateStillActive,
  parseEventDateAsLocalDay,
} from "../../utils/eventDateUtils";
import { useGetAllPosts } from "../../api/queries/useGetAllPosts";
import InfiniteScroll from "react-infinite-scroll-component";
import { isEventEarlyBirdConfigured } from "../../utils/eventEarlyBirdUtils";
import { formatCurrencyAmount } from "../../utils/formatCurrency";
import { useTranslation } from "react-i18next";
import { useTranslateDynamicFields } from "../../api/queries/useTranslateDynamicFields";

const PAGE_SIZE = 10;

const Posts = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAppContext();

  const [posts, setPosts] = useState<any[]>([]);
  const [hasMorePosts, setHasMorePosts] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [editPostData, setEditPostData] = useState<Post | null>(null);

  const { data: events } = useFindUpcomingEvents({
    pageSize: PAGE_SIZE,
    page: 1,
  });
  const event = useMemo(() => {
    const list = events?.data ?? [];
    const active = list
      .filter((e) => isEventDateStillActive(e.eventDate))
      .sort(
        (a, b) =>
          parseEventDateAsLocalDay(a.eventDate).getTime() -
          parseEventDateAsLocalDay(b.eventDate).getTime(),
      );
    return active[0] ?? ({} as Event);
  }, [events?.data]);

  const { data: postsData, isFetching , refetch: refetchPostsQuery } = useGetAllPosts({
    pageSize: PAGE_SIZE,
    page: currentPage,
  });

  useEffect(() => {
    if (!postsData) return;

    const newPosts = postsData.posts.data.map((post) => {
      const imageEntry = postsData.postImageUrls.find((img) => img.postId === post.id);
      return { ...post, ...imageEntry?.imageUrls };
    });

    setPosts((prev) => {
      // merge without duplicates
      const merged = [...prev, ...newPosts];
      return Array.from(new Map(merged.map((p) => [p.id, p])).values());
    });

    // if fewer than PAGE_SIZE returned, no more posts
    if (newPosts.length < PAGE_SIZE) {
      setHasMorePosts(false);
    }
  }, [postsData]);

  const handleRefetchPosts = async () => {
    setCurrentPage(1);
    setHasMorePosts(true);
    setPosts([]); // clear old posts
    await refetchPostsQuery(); // force query
  };

  const loadMorePosts = () => {
    if (!isFetching && hasMorePosts) {
      setCurrentPage((prev) => prev + 1);
    }
  };
  const sidebarFullName = useMemo(() => {
    const first = user?.user?.firstName || "";
    const last = user?.user?.lastName || "";
    const formattedFirst =
      first.charAt(0).toUpperCase() + first.slice(1);
    const formattedLast =
      last.charAt(0).toUpperCase() + last.slice(1);
    return `${formattedFirst} ${formattedLast}`.trim();
  }, [user?.user?.firstName, user?.user?.lastName]);

  const { data: translatedSidebar } = useTranslateDynamicFields(
    {
      fullName: sidebarFullName,
      studyInterest: user?.user?.studyInterest || "",
      personalPositionCompany: user?.user?.personalPositionCompany || "",
      businessSectorIndustry: user?.user?.businessSectorIndustry || "",
      eventName: event?.name || "",
      eventDescription: event?.description || "",
      eventLocation: event?.location || "",
    },
    i18n.resolvedLanguage || i18n.language
  );
  const tx = (key: keyof NonNullable<typeof translatedSidebar>, fallback?: string) =>
    (translatedSidebar as any)?.[key] || fallback || "";

  if (!user || Object.keys(user).length === 0) {
    return (
      <div className="container p-4 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{t("feed.loading")}</span>
        </div>
        <p>{t("feed.loadingYourPosts")}</p>
      </div>
    );
  }

  return (
    <div className="d-flex justify-content-center w-100 gap-4 p-4 pt-5 pt-md-5 m-0">
      <div className="w-100">
        {user?.user?.isAdmin === 1 && <CreatePostBox setEditPostData={setEditPostData} editPostData={editPostData} />}

        <InfiniteScroll
          dataLength={posts.length}
          next={loadMorePosts}
          hasMore={hasMorePosts}
          loader={<div className="text-center p-3">{t("feed.loadingMorePosts")}</div>}
          endMessage={<div className="text-center p-3">{t("feed.noMorePosts")}</div>}
        >
          {posts.map((post) => (
            <PostCard setEditPostData={setEditPostData} refetchPosts={handleRefetchPosts} key={post.id} post={post} />
          ))}
        </InfiniteScroll>
      </div>

      {/* Sidebar unchanged */}
      <div
        style={{ maxWidth: "28%", width: "100%" }}
        className="d-none d-lg-flex flex-column gap-4"
      >
        {/* Profile Card */}
        <div>
          <div className="card shadow border-0 pb-2">
            <div>
              <div
                className={`${
                  user.imageUrls.BannerImage ? "" : "gradient-bg"
                } position-relative profile-banner rounded-top d-flex justify-content-center align-items-center`}
                style={{
                  backgroundImage: `url(${user.imageUrls?.BannerImage ?? ""})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  width: "100%",
                  height: "100%",
                  aspectRatio: "3 / 1",
                }}
              >
                {!user.imageUrls.BannerImage && (
                  <div className="d-flex h-100 w-100 justify-content-center align-items-center">
                    <img
                      src="/SMBF-logo-white.png"
                      alt={t("nav.logoAlt")}
                      className="w-25 h-25"
                    />
                  </div>
                )}
                <div className="position-absolute translate-middle post-image-container">
                  <div className="position-relative w-100 h-100">
                    <Image
                      src={
                        user.imageUrls.ProfessionalPhoto ||
                        user.imageUrls.PersonalProfessionalPhoto ||
                        "/user.png"
                      }
                      roundedCircle
                      alt={t("dashboard.profilePhotoAlt")}
                      className="post-profile-image w-100 h-100 shadow-sm border border-2 border-white p-1"
                    />
                  </div>
                </div>
              </div>
              <div style={{ paddingTop: "12%" }} className="mx-3 flex-grow-1">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex flex-column mt-lg-3">
                    <a
                      className="mb-0 fs-3 fw-bold text-dark cursor-pointer text-decoration-none"
                      href="/my-account"
                    >
                      {tx("fullName", sidebarFullName)}
                    </a>
                    <span>
                      {user.user.membershipName === "Youth Membership"
                        ? user.user.studyInterest &&
                          tx(
                            "studyInterest",
                            user.user.studyInterest.charAt(0).toUpperCase() +
                              user.user.studyInterest.slice(1)
                          )
                        : user.user.personalPositionCompany &&
                          tx(
                            "personalPositionCompany",
                            user.user.personalPositionCompany
                              .charAt(0)
                              .toUpperCase() +
                              user.user.personalPositionCompany.slice(1)
                          )}
                    </span>
                    {user.user.businessSectorIndustry && (
                      <div className="d-flex gap-1">
                        <span className="text-muted">
                          {tx("businessSectorIndustry", user.user.businessSectorIndustry)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Event Card */}
        <div className="card shadow-lg border-0">
          <h3 className="p-3 m-0 rounded-top gradient-bg">{t("feed.upcomingEvent")}</h3>
          <div className="card-body d-flex flex-column justify-content-between">
            <div className="d-flex justify-content-between flex-column gap-3">
              <div>
                <h5 className="card-title fw-bold">{tx("eventName", event.name)}</h5>
                <p className="card-text text-muted">{tx("eventDescription", event.description)}</p>
                <p className="small text-secondary">
                  <strong>{t("events.location")}:</strong> {tx("eventLocation", event.location) || t("common.na")}
                </p>
                <p className="small text-secondary">
                  <strong>{t("events.eventDate")}:</strong>{" "}
                  {event.eventDate ? new Date(event.eventDate).toLocaleDateString() : t("common.na")}
                </p>
              </div>
              <div className="px-1">
                <ul className="list-unstyled mb-3 small">
                  <li>
                    <strong>{t("events.price")}:</strong>{" "}
                    {formatCurrencyAmount(event.price, event.currency ?? "gbp")}
                  </li>
                  <li>
                    <strong>{t("events.earlyBirdDiscount")}:</strong>{" "}
                    {formatCurrencyAmount(
                      event.earlyBirdPrice,
                      event.currency ?? "gbp",
                    )}
                  </li>
                  <li>
                    {event.earlyBirdDateEnd &&
                    new Date(event.earlyBirdDateEnd) > new Date() ? (
                      <>
                        <strong>{t("events.earlyBirdEndsOn")}: </strong>
                        {new Date(event.earlyBirdDateEnd).toLocaleDateString()}
                      </>
                    ) : (
                      <span className="text-danger">{t("events.earlyBirdEnded")}</span>
                    )}
                  </li>
                </ul>
              </div>
            </div>
            <a className="text-center" href="/event-booking">
              {t("feed.exploreMore")}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Posts;